import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChange, getCurrentUser } from '../firebase/auth';
import { getUserById, createOrUpdateUser, isAdminEmail, determineUserRole } from '../firebase/users';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null); // Full profile from Firestore
  const [role, setRole] = useState(null); // 'admin' | 'manager' | 'user'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (authUser) => {
      try {
        if (authUser) {
          setUser(authUser);

          // Create or update user in Firestore
          await createOrUpdateUser(authUser.uid, {
            email: authUser.email,
            displayName: authUser.displayName || authUser.email?.split('@')[0],
            photoURL: authUser.photoURL,
            provider: authUser.providerData?.[0]?.providerId || 'email'
          });

          // Fetch full user profile
          const profileResult = await getUserById(authUser.uid);
          if (profileResult.user) {
            setUserProfile(profileResult.user);
            setRole(profileResult.user.role || 'user');
          } else {
            // Determine role for new user
            const userRole = await determineUserRole(authUser);
            setRole(userRole);
          }
        } else {
          setUser(null);
          setUserProfile(null);
          setRole(null);
        }
      } catch (err) {
        console.error('Auth state handler error:', err);
        // Recover from corrupted auth state (e.g. payload decode errors)
        setUser(null);
        setUserProfile(null);
        setRole(null);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Refresh user profile
  const refreshUserProfile = async () => {
    if (!user) return;
    
    const profileResult = await getUserById(user.uid);
    if (profileResult.user) {
      setUserProfile(profileResult.user);
      setRole(profileResult.user.role || 'user');
    }
  };

  // Check permissions
  const isAdmin = role === 'admin';
  const isManager = role === 'manager';
  const isUser = role === 'user';
  
  // Manager's company ID
  const managerCompanyId = isManager && userProfile?.companyId ? userProfile.companyId : null;
  
  // Permission checks
  const canManageCompany = (companyId) => {
    return isAdmin || (isManager && managerCompanyId === companyId);
  };
  
  const canManageAllCompanies = () => isAdmin;
  const canAssignManagers = () => isAdmin;
  const canViewAllBookings = () => isAdmin;
  const canViewCompanyBookings = (companyId) => isAdmin || (isManager && managerCompanyId === companyId);

  const value = {
    user,
    userProfile,
    role,
    loading,
    currentUser: getCurrentUser(),
    
    // Role checks
    isAdmin,
    isManager,
    isUser,
    managerCompanyId,
    
    // Permission helpers
    canManageCompany,
    canManageAllCompanies,
    canAssignManagers,
    canViewAllBookings,
    canViewCompanyBookings,
    
    // Utilities
    refreshUserProfile,
    isAdminEmail,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
